import { isFilterWithPayload } from "@pages/Dashboard/WidgetEdit/FilterPanel/constants";
import {
  FiltersEntity,
  WidgetCreationEntity,
  WidgetEntity,
  WidgetScreen,
  WidgetScreenForUpdate,
} from "./types";
import { sfcountries } from "@assets/countries";

// Used to comply with id requirement of typescript, and easily delete id keys when needed.
// When present, the userWidgetId key should always be deleted to force the creation of a new widget on the backend side;
export const FAKE_WIDGET_ID = -1;

export const screensFEtoBE = (
  pages: UserWidgetScreen[],
  userId: string
): WidgetScreenForUpdate[] =>
  pages.map(({ userWidgetScreenId, label, widgets }, screenOrder) => ({
    id: userWidgetScreenId !== FAKE_WIDGET_ID ? userWidgetScreenId : undefined,
    screenOrder,
    userId,
    label,
    userWidgets: widgetsFEtoBE(widgets),
  }));

const widgetsFEtoBE = (widgets: UserWidget[]): WidgetCreationEntity[] =>
  widgets.map(({ label, widgetType, filters }, widgetOrder) => ({
    widgetOrder,
    label,
    widgetType,
    widgetFilters: filtersFEtoBE(filters),
  }));

export const filtersFEtoBE = (filters: WidgetFilter[]): FiltersEntity => {
  const temp: FiltersEntity = {};
  filters.map(({ filterValue, filterType, payload }) => {
    temp[filterType] = JSON.stringify({ filterValue, payload });
  });
  return temp;
};

export const screensBEtoFE = (pages: WidgetScreen[]): UserWidgetScreen[] =>
  (pages as Required<WidgetScreen>[])
    .sort(({ screenOrder: a }, { screenOrder: b }) => Math.sign(a - b))
    .map(({ label, id, userWidgets }) => ({
      label,
      userWidgetScreenId: id,
      widgets: widgetsBEtoFE(userWidgets !== null ? userWidgets : []),
    }));
const asFilterEntity = (
  filters: WidgetEntity["widgetFilters"]
): FiltersEntity => {
  if (filters === null || filters === undefined) return {};
  if (typeof filters === "string") return JSON.parse(filters);
  return filters;
};
const widgetsBEtoFE = (widgets: WidgetEntity[]): UserWidget[] =>
  widgets
    .sort(({ widgetOrder: a }, { widgetOrder: b }) => Math.sign(a - b))
    .map(({ label, widgetType, id, widgetFilters }) => ({
      label,
      userWidgetId: id,
      widgetType: widgetType as UserWidgetType,
      filters: filtersBEtoFE(
        widgetType as UserWidgetType,
        asFilterEntity(widgetFilters)
      ),
    }));
const isParsablePayload = (
  widgetType: UserWidgetType,
  filterType: FilterTypes,
  filterValue: string
) => {
  if (widgetType === "Meetings" || widgetType === "Tickets") {
    if (
      filterType === "time" &&
      ((filterValue as MeetingsTimeFilter) === "Custom time period" ||
        (filterValue as TicketsTimeFilter) === "Custom time period")
    )
      return true;
  }
  return false;
};
const payloadParser = (
  widgetType: UserWidgetType,
  filterType: FilterTypes,
  filterValue: string,
  payload: unknown
) => {
  if (!isFilterWithPayload(widgetType, filterType, filterValue)) return;
  if (
    isParsablePayload(widgetType, filterType, filterValue) &&
    typeof payload === "string"
  )
    return JSON.parse(payload);
  return payload;
};
const filtersBEtoFE = (
  widgetType: UserWidgetType,
  filters: FiltersEntity
): WidgetFilter[] =>
  Object.keys(filters)
    .map((filterType: string) => {
      // Making sure that we don't have unparsed data being sent through as filters is technically a Map<string,string> on the backend side;
      const f: FiltersEntity[typeof filterType] = filters[filterType] as any;
      let parsed = f;
      if (typeof parsed === "string")
        try {
          parsed = JSON.parse(parsed) as FiltersEntity[typeof filterType];
        } catch (e) {
          console.log(e);
        }

      if (parsed === undefined || typeof parsed === "string") return;

      // backward compatibility with 6.9
      if (
        ["room.location.country", "location.country", "country"].includes(
          filterType
        )
      ) {
        if (parsed.filterValue.length > 2) {
          let country = parsed.filterValue;
          parsed.filterValue =
            sfcountries.find((c) => c.name == country)?.alpha2 ?? country;
        }
      }
      return {
        filterType,
        filterValue: parsed.filterValue,
        payload: payloadParser(
          widgetType,
          filterType as FilterTypes,
          parsed.filterValue,
          parsed.payload
        ),
      };
    })
    .filter((v) => v !== undefined) as WidgetFilter[];
